import { z } from 'zod';
import { seoSchema } from './landing.ts';
import { richDocSchema, richToText, type RichDoc } from './rich.ts';

const text = (max: number) => z.string().trim().max(max);

export const contentTypes = ['CASE_STUDY', 'ARTICLE'] as const;
export const contentTypeSchema = z.enum(contentTypes);
export type ContentTypeValue = z.infer<typeof contentTypeSchema>;
/** Public URL family of each type; the item slug follows. */
export const contentInfo: Record<ContentTypeValue, { label: string; plural: string; prefix: string }> = {
  CASE_STUDY: { label: 'Case study', plural: 'Case studies', prefix: 'case-studies/' },
  ARTICLE: { label: 'Article', plural: 'Articles', prefix: 'insights/' },
};

/** Images are chosen from the media library: stored as the media id, rendered as /media/<id>/<file>. */
const mediaRef = z.object({ mediaId: z.string().min(1).max(40), alt: text(200).optional() });

/** A claim that could mislead if invented. It shows publicly only when verified, with where the number comes from. */
const verifiedFields = { verified: z.boolean().default(false), source: text(200).optional() };

export const caseStudyDataSchema = z.object({
  title: text(120).min(1),
  summary: text(300).min(1),
  /** Named clients need written permission on record; otherwise publish anonymously ("A logistics company"). */
  clientName: text(120).optional(),
  clientDisclosure: z.enum(['named', 'anonymous']).default('anonymous'),
  clientPermission: z.boolean().default(false),
  clientDescriptor: text(120).optional(),
  industry: text(60).optional(),
  serviceKeys: z.array(text(40)).max(6).default([]),
  technologies: z.array(text(40)).max(20).default([]),
  duration: text(60).optional(),
  heroImage: mediaRef.optional(),
  challenge: richDocSchema,
  solution: richDocSchema,
  results: richDocSchema,
  metrics: z.array(z.object({ label: text(80).min(1), value: text(40).min(1), ...verifiedFields })).max(6).default([]),
  testimonial: z.object({ quote: text(500).min(1), name: text(80).min(1), role: text(80).optional(), ...verifiedFields }).optional(),
  seo: seoSchema,
});
export type CaseStudyData = z.infer<typeof caseStudyDataSchema>;

export const articleDataSchema = z.object({
  title: text(140).min(1),
  summary: text(300).min(1),
  body: richDocSchema,
  authorName: text(80).min(1),
  authorRole: text(80).optional(),
  tags: z.array(text(40)).max(10).default([]),
  heroImage: mediaRef.optional(),
  seo: seoSchema,
});
export type ArticleData = z.infer<typeof articleDataSchema>;

export const contentDataSchema = (type: ContentTypeValue) => (type === 'CASE_STUDY' ? caseStudyDataSchema : articleDataSchema);

export const contentCreateSchema = z.object({
  type: contentTypeSchema,
  title: text(140).min(2),
  locale: text(10).min(2),
  slug: text(120).optional(),
});
export type ContentCreate = z.infer<typeof contentCreateSchema>;
export const contentSaveSchema = z.object({ data: z.record(z.string(), z.unknown()), expectedRevision: z.number().int().min(0) });
export const contentMetaSchema = z.object({ slug: text(120).min(1) });

export const contentListItemSchema = z.object({
  id: z.string(),
  type: contentTypeSchema,
  title: z.string(),
  slug: z.string(),
  locale: z.string(),
  archived: z.boolean(),
  draftRevision: z.number().int(),
  publishedVersionNumber: z.number().int().nullable(),
  hasUnpublishedChanges: z.boolean(),
  updatedAt: z.string(),
});
export type ContentListItem = z.infer<typeof contentListItemSchema>;

export const contentDraftSchema = z.object({
  id: z.string(),
  type: contentTypeSchema,
  locale: z.string(),
  slug: z.string(),
  publicPath: z.string(),
  revision: z.number().int(),
  data: z.record(z.string(), z.unknown()),
  archived: z.boolean(),
  publishedVersionNumber: z.number().int().nullable(),
  updatedAt: z.string(),
});
export type ContentDraft = z.infer<typeof contentDraftSchema>;

/** Publish gate: returns human-readable blockers. Unverified claims are not blockers: they simply stay hidden publicly. */
export function contentBlockers(type: ContentTypeValue, raw: unknown): string[] {
  const parsed = contentDataSchema(type).safeParse(raw);
  if (!parsed.success) return parsed.error.issues.slice(0, 8).map((i) => `${i.path.join('.') || 'content'}: ${i.message}`);
  const out: string[] = [];
  const d = parsed.data;
  const placeholder = (s: string | undefined) => (s ?? '').startsWith('[Edit]');
  const docs: [string, RichDoc][] = type === 'CASE_STUDY' ? [['Challenge', (d as CaseStudyData).challenge], ['Solution', (d as CaseStudyData).solution], ['Results', (d as CaseStudyData).results]] : [['Body', (d as ArticleData).body]];
  for (const [label, doc] of docs) {
    const t = richToText(doc);
    if (!t) out.push(`${label} is empty`);
    else if (placeholder(t)) out.push(`${label} still contains placeholder copy`);
  }
  if (placeholder(d.title) || placeholder(d.summary)) out.push('Title or summary still contains placeholder copy');
  if (type === 'CASE_STUDY') {
    const c = d as CaseStudyData;
    if (c.clientDisclosure === 'named' && (!c.clientName || !c.clientPermission)) out.push('A named client needs the client’s name and confirmation that written permission is on record. Otherwise publish anonymously.');
    if (c.clientDisclosure === 'anonymous' && !c.clientDescriptor) out.push('Describe the anonymous client (for example “A logistics company with 400 staff”)');
    if (c.heroImage && !c.heroImage.alt) out.push('The hero image needs alt text');
  } else if (d.heroImage && !(d as ArticleData).heroImage?.alt) out.push('The hero image needs alt text');
  if (d.seo.robots !== 'index') out.push('Search indexing is off for this page');
  return out;
}

/** What visitors see: only verified claims survive. */
export function publicCaseStudy(d: CaseStudyData): CaseStudyData {
  return {
    ...d,
    clientName: d.clientDisclosure === 'named' && d.clientPermission ? d.clientName : undefined,
    metrics: d.metrics.filter((m) => m.verified),
    testimonial: d.testimonial?.verified ? d.testimonial : undefined,
  };
}

// ---------- Media (step 1 §97) ----------
export const mediaAssetSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  altText: z.string().nullable(),
  tags: z.array(z.string()),
  archived: z.boolean(),
  url: z.string(),
  createdAt: z.string(),
  usageCount: z.number().int().default(0),
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;
export const mediaUpdateSchema = z.object({ altText: text(200).optional(), tags: z.array(text(30)).max(10).optional(), filename: text(120).min(1).optional() });
export const MEDIA_MAX_BYTES = 8 * 1024 * 1024;
export const MEDIA_TYPES = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif' } as const;
export const mediaUrl = (id: string, filename: string, width?: number) => `/media/${id}/${encodeURIComponent(filename)}${width ? `?w=${width}` : ''}`;
export const isMediaPath = (href: string) => /^\/media\/[A-Za-z0-9]+\//.test(href);

/** What the public renderer receives for a published case study or article. Unverified claims are already removed. */
export interface PublishedContent {
  type: ContentTypeValue;
  locale: string;
  slug: string;
  title: string;
  versionNumber: number;
  publishedAt: string;
  canonical: string;
  data: CaseStudyData | ArticleData;
  hero?: { url: string; alt: string; width: number | null; height: number | null };
  alternates: { locale: string; hreflang: string; path: string }[];
  jsonLd: Record<string, unknown>[];
  breadcrumbs: { name: string; path: string }[];
  /** Other published items to link to (same type, same language). */
  related: { title: string; summary: string; href: string }[];
}
